import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { startAuthentication } from '@simplewebauthn/browser';
import type { LoginMfaChallengeProps, PasskeyOption } from './types';
import { resolveOpeningMfaMethod, writeLastMfaMethod, readLastMfaMethod } from './lastMfaMethod';
import { abortPasskeyCeremony, hasPasskeyCeremonyLock, runExclusivePasskeyCeremony } from './passkeyCeremony';
import { resolveQixitAuthenticatorIconUrl } from './qixitBranding';

/** Survives React Strict Mode remounts so we only auto-prompt once per MFA challenge. */
const autoStartedKeys = new Set<string>();

function isUserCancelOrAbort(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  const name = e?.name || '';
  const message = String(e?.message || '');
  return (
    name === 'NotAllowedError' ||
    name === 'AbortError' ||
    /cancel|not allowed|abort/i.test(message)
  );
}

function isOsPasskeyFailure(err: unknown): boolean {
  const message = String((err as { message?: string } | null)?.message || '');
  const name = (err as { name?: string } | null)?.name || '';
  return (
    name === 'InvalidStateError' ||
    name === 'UnknownError' ||
    /something went wrong|problem saving|timed out|not allowed/i.test(message)
  );
}

/** Synced PM keys often fail auto-start; prefer chooser / TOTP unless a device-bound key exists. */
function looksSyncedPasswordManagerPasskey(pk: PasskeyOption): boolean {
  const label = (pk.label || '').toLowerCase();
  // Phone QR enrollments are labeled "Phone (Google Password Manager)" — not a PC synced key.
  if (/\bphone\b/.test(label) && !label.includes('windows hello')) return false;
  return /password manager|google password|microsoft password|\bsynced\b/.test(label);
}

/** True platform / security-key keys that are safe to auto-prompt (not synced PM / phone GPM). */
function looksDeviceBoundPasskey(pk: PasskeyOption): boolean {
  const label = (pk.label || '').toLowerCase();
  if (looksSyncedPasswordManagerPasskey(pk)) return false;
  if (/\bphone\b/.test(label)) return false;
  return (
    label.includes('windows hello') ||
    label.includes('this device') ||
    label.includes('this computer') ||
    label.includes('security key') ||
    label.includes('platform')
  );
}

/** True for phone/QR / security-key hybrid paths — not synced password-manager passkeys. */
function looksPhoneOrHybridPasskey(pk: PasskeyOption): boolean {
  const transports = (pk.transports || '').toLowerCase();
  const label = (pk.label || '').toLowerCase();
  if (looksSyncedPasswordManagerPasskey(pk)) return false;
  return transports.includes('hybrid') || /\bphone\b/.test(label);
}

function passkeyIcon(transports?: string) {
  const t = (transports || '').toLowerCase();
  if (t.includes('hybrid')) return <PhoneAndroidIcon />;
  if (t.includes('usb') || t.includes('nfc') || t.includes('ble')) return <VpnKeyIcon />;
  return <FingerprintIcon />;
}

/**
 * Shared MFA challenge UX: open the method last used on this device when still
 * available, else server preferred. Auto-start passkey when that is the opening
 * method (unless only synced PM keys and TOTP is preferred by resilience rules).
 * Cancel/fail always offers every method.
 */
export function LoginMfaChallenge<TComplete = unknown>({
  userId,
  preferredMethod,
  availableMethods,
  availablePasskeys = [],
  mfaScopeCredentialId,
  adapters,
  onComplete,
  onCancel,
  autoStartKey,
}: LoginMfaChallengeProps<TComplete>) {
  const has = (m: string) => availableMethods.map((x) => x.toLowerCase()).includes(m);
  const passkeys =
    availablePasskeys.length > 0
      ? availablePasskeys
      : has('passkey')
        ? [{ passkeyId: '', label: 'Use a passkey' } as PasskeyOption]
        : [];

  const lastUsedMethod = readLastMfaMethod(userId);
  const hasPhonePasskey = passkeys.some(looksPhoneOrHybridPasskey);
  // Stale "phone" memory from when synced PM keys were mis-tagged as hybrid — treat as passkey.
  const effectiveLast =
    lastUsedMethod === 'phone' && !hasPhonePasskey && has('passkey') ? 'passkey' : lastUsedMethod;
  const preferred = (preferredMethod || '').trim().toLowerCase();
  // Server PreferredMethod already applies empty-preference fallback. Honor explicit preference;
  // otherwise last-used on this device, then server preferred / resolveOpeningMfaMethod.
  const openingMethod =
    preferred === 'passkey' && has('passkey')
      ? 'passkey'
      : preferred === 'totp' && has('totp')
        ? 'totp'
        : effectiveLast === 'passkey' && has('passkey')
          ? 'passkey'
          : effectiveLast === 'totp' && has('totp')
            ? 'totp'
            : resolveOpeningMfaMethod(userId, availableMethods, preferredMethod);

  // Phone/QR intermediary only when the last successful sign-in on this device used hybrid/QR.
  // Password-manager / local passkeys auto-start the OS ceremony (no extra in-app sheet).
  const showPhoneContinue = has('passkey') && openingMethod === 'phone' && hasPhonePasskey;

  // When default/opening method is passkey, open the OS PIN ceremony immediately.
  // Cancel / "Try another way" returns to authenticator, backup codes, and other passkeys.
  const shouldAutoStartPasskey =
    !showPhoneContinue && openingMethod === 'passkey' && has('passkey');

  const opensCodeEntry =
    openingMethod === 'totp' || openingMethod === 'backup_code';

  const pickAutoStartPasskey = (): PasskeyOption | undefined => {
    const withId = passkeys.filter((p) => !!p.passkeyId);
    if (withId.length === 0) return passkeys[0];
    return (
      withId.find(looksSyncedPasswordManagerPasskey) ||
      withId.find(looksDeviceBoundPasskey) ||
      withId[0]
    );
  };

  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showChooser, setShowChooser] = useState(
    !showPhoneContinue && !shouldAutoStartPasskey && !opensCodeEntry
  );
  const [activeMethod, setActiveMethod] = useState<string | null>(() => {
    if (showPhoneContinue) return 'phone_prompt';
    if (shouldAutoStartPasskey) return 'passkey';
    if (opensCodeEntry) return openingMethod;
    return null;
  });
  const [code, setCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);

  const passkeyAttemptIdRef = useRef(0);
  const completedRef = useRef(false);
  const lastCeremonyHybridRef = useRef(false);
  const dedupeKey = autoStartKey || userId;

  // Do not abort WebAuthn in effect cleanup — React Strict Mode remounts and parent
  // re-renders were cancelling the first Windows PIN mid-entry ("Something went wrong").
  // Clear stale locks when starting a ceremony (runPasskey) and on cancel / leave MFA.
  useEffect(() => {
    return () => {
      passkeyAttemptIdRef.current += 1;
    };
  }, [dedupeKey]);

  const openChooser = (message?: string | null) => {
    setShowChooser(true);
    setActiveMethod(null);
    setLoading(false);
    if (message) setInfo(message);
  };

  const finish = async (method: string, payload: { code?: string; assertionResponse?: unknown }) => {
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const result = await adapters.completeMethod({
        method,
        code: payload.code,
        assertionResponse: payload.assertionResponse,
      });
      const remembered =
        method === 'passkey' && lastCeremonyHybridRef.current ? 'phone' : method;
      writeLastMfaMethod(userId, remembered);
      completedRef.current = true;
      onComplete(result);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Verification failed.');
      openChooser('Choose a passkey or another verification method below.');
    } finally {
      setLoading(false);
    }
  };

  const runPasskey = async (opts?: {
    fromAuto?: boolean;
    passkeyId?: string;
    preferPlatform?: boolean;
    preferHybrid?: boolean;
    allOptions?: boolean;
    /** Synced Google/Microsoft PM — never pin credential id (Win11 Continue→fail loop). */
    syncedPasswordManager?: boolean;
  }) => {
    if (completedRef.current) return;

    const attemptId = ++passkeyAttemptIdRef.current;
    setLoading(true);
    setError(null);
    setInfo(null);
    setActiveMethod('passkey');
    setShowChooser(false);

    // Auto-start: account allowlist, no transport bias (avoids Win11 Continue loops).
    // Explicit phone / "show all": preferHybrid so OS offers QR / phone.
    // Per-key click: pin that credential id when known (non-hybrid, non-synced-PM only).
    const preferHybrid = !!opts?.preferHybrid || !!opts?.allOptions;
    const preferPlatform = opts?.allOptions || preferHybrid
      ? false
      : opts?.preferPlatform !== undefined
        ? !!opts.preferPlatform
        : false;
    // Hybrid/QR / "show all": do not pin. Auto-start and per-key click pin the credential when
    // known so Win11 goes to PIN (allowLen:1) instead of a multi-key Continue sheet.
    const passkeyId =
      preferHybrid || opts?.allOptions
        ? undefined
        : opts?.passkeyId || undefined;

    lastCeremonyHybridRef.current = preferHybrid;

    try {
      const hadLockBefore = hasPasskeyCeremonyLock();
      if (hadLockBefore) {
        abortPasskeyCeremony('runPasskey-before-get');
        // Brief pause so Windows finishes cancelling any prior get() before a new one
        // (overlapping ceremonies → first PIN "Something went wrong").
        await new Promise((r) => window.setTimeout(r, 200));
      }
      if (attemptId !== passkeyAttemptIdRef.current) return;

      const assertion = await runExclusivePasskeyCeremony(async () => {
        const options = await adapters.getPasskeyAuthOptions({
          userId,
          passkeyId,
          preferPlatform,
          preferHybrid,
          scopeCredentialId: mfaScopeCredentialId,
        });
        if (attemptId !== passkeyAttemptIdRef.current) return null;
        return startAuthentication({ optionsJSON: options });
      });
      if (!assertion) return;
      if (attemptId !== passkeyAttemptIdRef.current || completedRef.current) return;

      await finish('passkey', { assertionResponse: assertion });
    } catch (err: any) {
      if (attemptId !== passkeyAttemptIdRef.current) return;
      if (completedRef.current) return;

      if (isUserCancelOrAbort(err)) {
        setError(null);
        openChooser(
          opts?.fromAuto
            ? 'Passkey skipped. Choose any passkey or another method below.'
            : 'Choose a verification method below.'
        );
      } else if (isOsPasskeyFailure(err) || opts?.fromAuto) {
        setError(
          'That passkey prompt failed. Pick another passkey option below, or use authenticator / backup code.'
        );
        openChooser(null);
      } else {
        setError(err?.response?.data?.error || err?.message || 'Passkey failed.');
        openChooser(null);
      }
    }
  };

  useEffect(() => {
    if (!shouldAutoStartPasskey) return;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      if (autoStartedKeys.has(dedupeKey)) return;
      autoStartedKeys.add(dedupeKey);

      const primary = pickAutoStartPasskey();
      void runPasskey({
        fromAuto: true,
        allOptions: false,
        preferPlatform: false,
        preferHybrid: primary ? looksPhoneOrHybridPasskey(primary) : false,
        passkeyId: primary?.passkeyId || undefined,
        syncedPasswordManager: primary ? looksSyncedPasswordManagerPasskey(primary) : false,
      });
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dedupeKey, shouldAutoStartPasskey]);

  const requestEmail = async () => {
    if (!adapters.requestEmailCode) return;
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const res = await adapters.requestEmailCode();
      setMaskedEmail(res.maskedEmail);
      setActiveMethod('email_recovery');
      setShowChooser(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not send recovery email.');
      openChooser(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={(e) => e.preventDefault()} sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
        <Box
          component="img"
          src={resolveQixitAuthenticatorIconUrl(true)}
          alt="Qixit"
          sx={{ width: 72, height: 72, objectFit: 'contain' }}
          onError={(e) => {
            const el = e.currentTarget;
            if (el.src !== resolveQixitAuthenticatorIconUrl(false)) {
              el.src = resolveQixitAuthenticatorIconUrl(false);
            }
          }}
        />
      </Box>
      <Typography variant="h5" align="center" gutterBottom>
        Verify it&apos;s you
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
        This account has extra security enabled.
      </Typography>
      {has('passkey') && has('totp') && (
        <Alert severity="info" sx={{ mb: 2 }}>
          If a passkey does not appear in this browser, use your authenticator app — password-manager
          passkeys are often browser-specific and will not lock you out of other devices.
        </Alert>
      )}
      {has('passkey') && !has('totp') && has('backup_code') && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No authenticator app on this account. If this browser cannot use your passkey, use a backup
          code — and enable an authenticator app in Mailbox Account Settings when you can.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {!error && info && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      {loading && activeMethod === 'passkey' && (
        <Stack alignItems="center" spacing={1} sx={{ my: 3 }}>
          <CircularProgress />
          <Typography variant="body2">Waiting for passkey…</Typography>
          <Button
            size="small"
            onClick={() => {
              passkeyAttemptIdRef.current += 1;
              abortPasskeyCeremony('try-another-way');
              openChooser(
                'Choose authenticator app, backup code, or try the passkey again below.'
              );
            }}
          >
            Try another way
          </Button>
        </Stack>
      )}

      {activeMethod === 'phone_prompt' && !loading && (
        <Stack spacing={1.5} sx={{ my: 1 }}>
          <Alert severity="info">
            Use your phone to approve sign-in. The next screen should offer a QR code — choose
            &quot;Use a phone, tablet, or security key&quot; if Windows lists local passkeys first.
          </Alert>
          <Button
            variant="contained"
            size="large"
            startIcon={<PhoneAndroidIcon />}
            onClick={() =>
              void runPasskey({
                fromAuto: false,
                preferHybrid: true,
                preferPlatform: false,
                allOptions: false,
              })
            }
          >
            Show QR code on this screen
          </Button>
          <Button size="small" onClick={() => openChooser(null)}>
            Try another way
          </Button>
        </Stack>
      )}

      {(showChooser || activeMethod === null) && !(loading && activeMethod === 'passkey') && (
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">Choose a verification method</Typography>
          {passkeys.map((pk) => (
            <Box key={pk.passkeyId || pk.label}>
              <Button
                fullWidth
                variant="contained"
                startIcon={passkeyIcon(pk.transports)}
                onClick={() =>
                  void runPasskey({
                    fromAuto: false,
                    passkeyId: pk.passkeyId || undefined,
                    preferHybrid: looksPhoneOrHybridPasskey(pk),
                    preferPlatform: false,
                    allOptions: false,
                    syncedPasswordManager: looksSyncedPasswordManagerPasskey(pk),
                  })
                }
                disabled={loading}
              >
                {pk.label}
              </Button>
              {looksSyncedPasswordManagerPasskey(pk) && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  On Windows, the first PIN sometimes fails inside the OS dialog — use Try again if prompted,
                  or use Authenticator app code below.
                </Typography>
              )}
            </Box>
          ))}
          {passkeys.length > 1 && (
            <Box>
              <Button
                fullWidth
                variant="outlined"
                onClick={() =>
                  void runPasskey({
                    fromAuto: false,
                    allOptions: true,
                    preferHybrid: true,
                    preferPlatform: false,
                  })
                }
                disabled={loading}
              >
                Phone or security key (QR / USB)
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Opens Windows for a phone QR or USB security key only. Password-manager keys (Microsoft /
                Google) are the buttons above — hybrid mode hides them on purpose.
              </Typography>
            </Box>
          )}
          {has('totp') && (
            <Button
              variant="outlined"
              onClick={() => {
                setActiveMethod('totp');
                setShowChooser(false);
                setInfo(null);
              }}
              disabled={loading}
            >
              Authenticator app code
            </Button>
          )}
          {has('backup_code') && (
            <Button
              variant="outlined"
              onClick={() => {
                setActiveMethod('backup_code');
                setShowChooser(false);
                setInfo(null);
              }}
              disabled={loading}
            >
              Backup code
            </Button>
          )}
          {has('email_recovery') && adapters.requestEmailCode && (
            <Button variant="text" onClick={() => void requestEmail()} disabled={loading}>
              Device not available — email me a code
            </Button>
          )}
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => {
              abortPasskeyCeremony();
              onCancel();
            }}
            sx={{ mt: 1 }}
          >
            Back to login
          </Link>
        </Stack>
      )}

      {(activeMethod === 'totp' || activeMethod === 'backup_code' || activeMethod === 'email_recovery') && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          {activeMethod === 'email_recovery' && maskedEmail && (
            <Alert severity="info">Code sent to {maskedEmail}</Alert>
          )}
          <TextField
            label={
              activeMethod === 'backup_code'
                ? 'Backup code'
                : activeMethod === 'email_recovery'
                  ? 'Email code'
                  : 'Authenticator code'
            }
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
            fullWidth
            autoFocus
            inputProps={{ inputMode: 'numeric' }}
          />
          <Button
            variant="contained"
            disabled={loading || code.length < 6}
            onClick={() => void finish(activeMethod, { code })}
          >
            {loading ? <CircularProgress size={22} /> : 'Verify'}
          </Button>
          <Button
            size="small"
            onClick={() => {
              setCode('');
              openChooser(null);
            }}
          >
            Try another way
          </Button>
        </Stack>
      )}
    </Box>
  );
}

export default LoginMfaChallenge;
